import React, { useState, useEffect, useCallback } from 'react';
import { Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';

const GOLD = '#C8922A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BD3 = '#F0EDE8';
const BG = '#FAF8F5';
const BG3 = '#EDEAE3';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#C0392B';
const GRN = '#2E7D4F';
const JPY_CNY_FALLBACK = 0.043;

const CAT_EMOJI = { watch: '⌚', jewelry: '💍', bag: '👜', other: '📦' };

function useCountdown(endAt) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!endAt) { setText(''); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('ENDED'); return; }
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      if (d > 0) setText(`${d}d ${h}h`);
      else if (h > 0) setText(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      else setText(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  return text;
}

function SessionGroupHeader({ group, onMandateGroup, ended }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countdown = useCountdown(ended ? null : group.end_at);
  const isLive = !ended && (group.is_live || (group.end_at && dayjs(group.end_at).isAfter(dayjs()) && dayjs(group.start_at).isBefore(dayjs())));
  const typeLabel = { watch: `⌚ ${t('browse.watches')}`, jewelry: `💍 ${t('browse.jewelry')}`, bag: `👜 ${t('browse.bags')}`, mixed: `📦 ${t('browse.other')}` }[group.type] || `📦 ${t('browse.other')}`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: ended ? '#3D3830' : INK, borderRadius: '4px 4px 0 0',
    }}>
      <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,.4)', letterSpacing: 2 }}>
        {(group.house || '').toUpperCase()}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#fff', flex: 1 }}>{group.session}</span>
      <span style={{
        fontSize: 10, padding: '2px 8px', borderRadius: 2, fontFamily: 'monospace',
        background: group.type === 'watch' ? 'rgba(43,92,230,.25)' : group.type === 'jewelry' ? 'rgba(103,65,217,.25)' : 'rgba(255,255,255,.1)',
        color: group.type === 'watch' ? '#7BA7F7' : group.type === 'jewelry' ? '#c8b8ff' : 'rgba(255,255,255,.6)',
      }}>
        {typeLabel}
      </span>
      {ended ? (
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', fontFamily: 'monospace' }}>
          {t('browse.ended')} · {group.end_at ? dayjs(group.end_at).format('M/D') : '--'}
        </span>
      ) : (
        <>
          {isLive && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#5FC98A', fontFamily: 'monospace' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5FC98A', animation: 'blink 1s infinite', display: 'inline-block' }} />
              {t('session.inProgress')}
            </span>
          )}
          {countdown && (
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(255,255,255,.5)' }}>
              {isLive ? countdown : countdown + ` ${t('watchlist.startsIn')}`}
            </span>
          )}
        </>
      )}
      <span style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>{group.items.length}{t('watchlist.itemsWatched')}</span>
      {!ended && (
        <button onClick={() => onMandateGroup(group)} style={{
          padding: '5px 12px', border: '1px solid rgba(255,255,255,.2)', borderRadius: 3,
          fontSize: 11, color: 'rgba(255,255,255,.7)', cursor: 'pointer', background: 'transparent',
        }}>🔨 {t('watchlist.batchMandateSession')}</button>
      )}
      {!ended && isLive && (
        <button onClick={() => navigate('/auction')} style={{
          padding: '5px 12px', background: GOLD, border: `1px solid ${GOLD}`, borderRadius: 3,
          fontSize: 11, color: '#fff', cursor: 'pointer',
        }}>● {t('watchlist.enterAuction')}</button>
      )}
    </div>
  );
}

function WatchlistCard({ item, onRemove, onMandate, ended, jpyCnyRate = JPY_CNY_FALLBACK }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const emoji = CAT_EMOJI[item.category] || '📦';
  const sealed = item.bid_type === 'sealed';
  const startPrice = item.start_price_jpy || 0;
  const currentPrice = item.current_price_jpy || item.current_price || item.start_price_jpy || 0;
  const cnyCurrent = Math.round(currentPrice * jpyCnyRate);
  const cd = useCountdown(ended ? null : item.end_at);
  const lotNo = item.box_no && item.item_no
    ? `${item.box_no}-${String(item.item_no).padStart(2, '0')}`
    : (item.box_no || item.external_lot_id || '');

  // 已结拍的 tab 里固定显示"已结拍"
  const statusLabel = ended ? t('browse.ended')
    : item.status === 'live' ? t('mandates.statusBidding')
    : item.status === 'soon' ? t('session.upcoming') : t('watchlist.futureSessions');
  const statusStyle = ended
    ? { background: 'rgba(26,22,18,.06)', color: INK4, border: 'none' }
    : item.status === 'live'
    ? { background: 'rgba(46,125,79,.2)', color: GRN, border: '1px solid rgba(46,125,79,.3)' }
    : item.status === 'soon'
    ? { background: 'rgba(200,146,42,.15)', color: GOLD, border: '1px solid rgba(200,146,42,.25)' }
    : { background: 'rgba(26,22,18,.06)', color: INK3, border: 'none' };

  let images = [];
  try { images = typeof item.image_urls === 'string' ? JSON.parse(item.image_urls) : (item.image_urls || []); } catch { }
  const imgSrc = images[0] || null;

  return (
    <div style={{
      border: `1px solid ${BD2}`, borderRadius: 4, overflow: 'hidden',
      transition: 'all .15s', cursor: 'pointer', background: '#fff',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(26,22,18,.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Image */}
      <div style={{ height: 140, background: BG3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, position: 'relative' }}>
        {imgSrc ? (
          <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: ended ? 'grayscale(40%)' : 'none' }} />
        ) : (
          <span style={{ opacity: ended ? 0.5 : 1 }}>{emoji}</span>
        )}
        <span style={{
          position: 'absolute', top: 8, left: 8,
          fontSize: 10, padding: '2px 7px', borderRadius: 2, fontFamily: 'monospace', fontWeight: 600,
          ...statusStyle,
        }}>{statusLabel}</span>
        <span onClick={e => { e.stopPropagation(); onRemove(item); }} style={{
          position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: '50%',
          background: 'rgba(255,255,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, cursor: 'pointer', color: RED, transition: 'all .15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >✕</span>
        {/* 成色徽章 */}
        {item.rank && (
          <span style={{
            position: 'absolute', bottom: 8, left: 8, minWidth: 22, height: 22, padding: '0 6px',
            borderRadius: 11, background: GRN, color: '#fff', fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace',
          }}>{item.rank}</span>
        )}
        {/* 剩余时间 */}
        {!ended && cd && (
          <span style={{
            position: 'absolute', bottom: 8, right: 8, padding: '2px 7px', borderRadius: 2,
            background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11, fontFamily: 'monospace',
          }}>⏱ {cd}</span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 10, color: INK4, letterSpacing: 1, marginBottom: 4, fontFamily: 'monospace' }}>
          {item.brand || item.category?.toUpperCase() || ''}
        </div>
        <div style={{
          fontSize: 13, fontWeight: 500, color: ended ? INK3 : INK, marginBottom: 4, lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {item.title_zh || item.title_ja || item.name || '--'}
        </div>
        {lotNo && (
          <div style={{ fontSize: 11, color: INK4, marginBottom: 6, fontFamily: 'monospace' }}>
            {t('watchlist.lotNo')}：{lotNo}
          </div>
        )}
        {/* 起拍价 */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 11, color: INK3 }}>{t('watchlist.startPrice')}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: ended ? INK3 : RED }}>
            ¥{startPrice.toLocaleString()}
          </span>
        </div>
        {/* 当前价（暗拍打码） */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 11, color: INK3 }}>{t('watchlist.currentPrice')}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 600, color: ended ? INK3 : INK }}>
                {sealed ? '＊＊＊＊' : `¥${currentPrice.toLocaleString()}`}
              </span>
            </div>
            {!sealed && <div style={{ fontSize: 11, color: INK4 }}>≈ ¥{cnyCurrent.toLocaleString()} CNY</div>}
          </div>
          {!!item.mandate && (
            <span style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 2,
              background: 'rgba(46,125,79,.1)', color: GRN, fontWeight: 600,
            }}>{t('watchlist.mandated')}</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '9px 12px', borderTop: `1px solid ${BD3}`, display: 'flex', gap: 6 }}>
        {!ended && !item.mandate && (
          <button onClick={e => { e.stopPropagation(); onMandate(item); }} style={{
            flex: 1, padding: 6, borderRadius: 3, fontSize: 12, cursor: 'pointer',
            border: 'none', background: INK, color: '#fff', textAlign: 'center',
          }}>🔨 {t('watchlist.mandate')}</button>
        )}
        {!ended && !!item.mandate && (
          <button onClick={e => { e.stopPropagation(); navigate('/mandates'); }} style={{
            flex: 1, padding: 6, borderRadius: 3, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${BD}`, background: '#fff', color: INK3, textAlign: 'center',
          }}>{t('watchlist.viewMandate')}</button>
        )}
        <button onClick={e => { e.stopPropagation(); navigate(`/lots/${item.lot_id || item.id}`); }} style={{
          flex: ended ? 1 : undefined, padding: 6, borderRadius: 3, fontSize: 12, cursor: 'pointer',
          border: `1px solid ${BD}`, background: '#fff', color: INK3, textAlign: 'center',
          minWidth: ended ? undefined : 60,
        }}>{t('common.detail')}</button>
      </div>
    </div>
  );
}

// 千旗式紧凑横排卡（移动端：小图在左、信息在右，一屏多条）
function CompactRow({ item, onRemove, ended, jpyCnyRate = JPY_CNY_FALLBACK }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const emoji = CAT_EMOJI[item.category] || '📦';
  const sealed = item.bid_type === 'sealed';
  const startPrice = item.start_price_jpy || 0;
  const currentPrice = item.current_price_jpy || item.current_price || item.start_price_jpy || 0;
  const cd = useCountdown(ended ? null : item.end_at);
  const lotNo = item.box_no && item.item_no
    ? `${item.box_no}-${String(item.item_no).padStart(2, '0')}`
    : (item.box_no || item.external_lot_id || '');
  let images = [];
  try { images = typeof item.image_urls === 'string' ? JSON.parse(item.image_urls) : (item.image_urls || []); } catch {}
  const imgSrc = images[0] || null;

  return (
    <div onClick={() => navigate(`/lots/${item.lot_id || item.id}`)} style={{
      display: 'flex', gap: 12, padding: 12, background: '#fff',
      borderBottom: `1px solid ${BD3}`, cursor: 'pointer',
    }}>
      {/* Thumb */}
      <div style={{ position: 'relative', width: 92, height: 92, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: BG3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {imgSrc
          ? <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: ended ? 'grayscale(40%)' : 'none' }} />
          : <span style={{ fontSize: 34 }}>{emoji}</span>}
        {item.rank && (
          <span style={{
            position: 'absolute', top: 4, right: 4, minWidth: 20, height: 20, padding: '0 5px',
            borderRadius: 10, background: GRN, color: '#fff', fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace',
          }}>{item.rank}</span>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: .5 }}>
            {item.house_name || item.house_code || ''}
          </span>
          <span onClick={e => { e.stopPropagation(); onRemove(item); }} style={{ fontSize: 15, color: INK4, padding: '0 4px', lineHeight: 1 }}>✕</span>
        </div>
        <div style={{
          fontSize: 13, fontWeight: 500, color: ended ? INK3 : INK, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{item.title_zh || item.title_ja || '--'}</div>
        {lotNo && <div style={{ fontSize: 11, color: INK4, marginTop: 2, fontFamily: 'monospace' }}>{t('watchlist.lotNo')}：{lotNo}</div>}
        <div style={{ marginTop: 'auto', paddingTop: 4, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '2px 10px' }}>
          <span style={{ fontSize: 11, color: INK3 }}>{t('watchlist.startPrice')}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: ended ? INK3 : RED }}>¥{startPrice.toLocaleString()}</span>
          <span style={{ fontSize: 11, color: INK3, marginLeft: 4 }}>{t('watchlist.currentPrice')}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: ended ? INK3 : INK }}>
            {sealed ? '＊＊＊＊' : `¥${currentPrice.toLocaleString()}`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          {ended
            ? <span style={{ fontSize: 11, color: INK4 }}>{t('browse.ended')}</span>
            : cd && <span style={{ fontSize: 12, fontFamily: 'monospace', color: RED }}>⏱ {cd}</span>}
          {!!item.mandate && (
            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 3, background: 'rgba(46,125,79,.1)', color: GRN, fontWeight: 600 }}>
              {t('watchlist.mandated')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Watchlist() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 返回恢复:从拍品详情(/lots/:id 路由页)back 回来时恢复 tab/排序/搜索词/滚动
  const [restored, saveListState] = useBackRestore();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => restored?.activeTab || 'active'); // 'active' | 'ended'
  const [sortKey, setSortKey] = useState(() => restored?.sortKey || 'time_left'); // 'time_left' | 'watched'
  const [keyword, setKeyword] = useState(() => restored?.keyword || '');
  useEffect(() => { saveListState({ activeTab, sortKey, keyword }); }, [saveListState, activeTab, sortKey, keyword]);
  useScrollRestore(restored, saveListState, !loading && watchlist.length > 0);
  const [JPY_CNY, setJpyCny] = useState(JPY_CNY_FALLBACK);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = data?.rate; if (r && r > 0) setJpyCny(Number(r));
    }).catch(() => {});
  }, []);

  const fetchWatchlist = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/watchlist', { params: { include_ended: 1 } });
      const items = data.data || data || [];
      setWatchlist(items);
    } catch (err) {
      console.error('Failed to fetch watchlist', err);
      setWatchlist([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  // ── 分组：进行中 vs 已结拍 ──────────────────────────────────────
  // API 字段: session_end / session_start / end_at(lot) / status(lot)
  const now = dayjs();
  const isEnded = (item) => {
    if (['ended', 'closed', 'sold'].includes(item.status)) return true;
    if (item.end_at && dayjs(item.end_at).isBefore(now)) return true;
    if (item.session_end && dayjs(item.session_end).isBefore(now)) return true;
    return false;
  };
  const activeItems = watchlist.filter(i => !isEnded(i));
  const endedItems  = watchlist.filter(i =>  isEnded(i));

  // 排序：剩余时间(快结拍在前) / 收藏时间(最近在前)
  const sortItems = (arr) => {
    const out = [...arr];
    if (sortKey === 'time_left') {
      out.sort((a, b) => {
        const ta = a.end_at ? dayjs(a.end_at).valueOf() : Infinity;
        const tb = b.end_at ? dayjs(b.end_at).valueOf() : Infinity;
        return ta - tb;
      });
    } else {
      out.sort((a, b) => {
        const ta = a.watched_at ? dayjs(a.watched_at).valueOf() : 0;
        const tb = b.watched_at ? dayjs(b.watched_at).valueOf() : 0;
        return tb - ta;
      });
    }
    return out;
  };
  // 收藏内搜索（标题/编号/品牌/拍卖行）
  const kw = keyword.trim().toLowerCase();
  const matchKw = (i) => {
    if (!kw) return true;
    const lotNo = i.box_no && i.item_no ? `${i.box_no}-${i.item_no}` : (i.box_no || i.external_lot_id || '');
    return [i.title_zh, i.title_ja, i.brand, i.house_name, lotNo]
      .some(v => String(v || '').toLowerCase().includes(kw));
  };
  const displayItems = sortItems((activeTab === 'active' ? activeItems : endedItems).filter(matchKw));

  // Group by session
  const groupBySession = (items) => {
    const groups = {};
    items.forEach(item => {
      const key = item.session_id || item.session_name || 'other';
      if (!groups[key]) {
        groups[key] = {
          session: item.session_name || t('watchlist.uncategorized'),
          house: item.house_name || '',
          type: item.session_type || 'mixed',
          end_at: item.session_end,          // API 字段名
          start_at: item.session_start,      // API 字段名
          is_live: item.session_status === 'active' || item.status === 'live',
          items: [],
        };
      }
      groups[key].items.push(item);
    });
    return Object.values(groups);
  };

  const groups = groupBySession(displayItems);
  // 组也按选定排序排列：剩余时间用组内最早结拍，收藏时间用组内最近收藏
  groups.sort((ga, gb) => {
    if (sortKey === 'time_left') {
      const ta = Math.min(...ga.items.map(i => i.end_at ? dayjs(i.end_at).valueOf() : Infinity));
      const tb = Math.min(...gb.items.map(i => i.end_at ? dayjs(i.end_at).valueOf() : Infinity));
      return ta - tb;
    }
    const ta = Math.max(...ga.items.map(i => i.watched_at ? dayjs(i.watched_at).valueOf() : 0));
    const tb = Math.max(...gb.items.map(i => i.watched_at ? dayjs(i.watched_at).valueOf() : 0));
    return tb - ta;
  });

  // Stats
  const totalCount    = watchlist.length;
  const liveCount     = activeItems.filter(i => i.status === 'live').length;
  const soonCount     = activeItems.filter(i => i.status === 'soon').length;
  const mandatedCount = displayItems.filter(i => !!i.mandate).length;

  const handleRemove = async (item) => {
    try {
      await api.delete(`/watchlist/${item.lot_id || item.id}`);
      setWatchlist(prev => prev.filter(i => (i.lot_id || i.id) !== (item.lot_id || item.id)));
      message.success(t('watchlist.remove'));
    } catch (err) {
      message.error(t('common.failed'));
    }
  };

  const handleMandate = async (item) => {
    try {
      await api.post('/mandates', { lot_id: item.lot_id || item.id });
      setWatchlist(prev => prev.map(i =>
        (i.lot_id || i.id) === (item.lot_id || item.id) ? { ...i, mandated: true } : i
      ));
      message.success(t('common.success'));
    } catch (err) {
      message.error(err.response?.data?.message || t('common.failed'));
    }
  };

  const handleMandateGroup = async (group) => {
    const unMandated = group.items.filter(i => !i.mandate);
    if (unMandated.length === 0) {
      message.info(t('common.noData'));
      return;
    }
    try {
      for (const item of unMandated) {
        await api.post('/mandates', { lot_id: item.lot_id || item.id });
      }
      setWatchlist(prev => {
        const ids = new Set(unMandated.map(i => i.lot_id || i.id));
        return prev.map(i => ids.has(i.lot_id || i.id) ? { ...i, mandated: true } : i);
      });
      message.success(t('watchlist.batchMandateSuccess', { session: group.session }));
    } catch (err) {
      message.error(t('common.failed'));
    }
  };

  const handleMandateAll = async () => {
    const unMandated = activeItems.filter(i => !i.mandate);
    if (unMandated.length === 0) { message.info(t('common.noData')); return; }
    try {
      for (const item of unMandated) {
        await api.post('/mandates', { lot_id: item.lot_id || item.id });
      }
      setWatchlist(prev => prev.map(i => ({ ...i, mandated: true })));
      message.success(t('common.success'));
    } catch (err) {
      message.error(t('common.failed'));
    }
  };

  const handleClearAll = async () => {
    const targets = displayItems;
    if (!window.confirm(t('watchlist.clearConfirm', { count: targets.length }))) return;
    try {
      for (const item of targets) {
        await api.delete(`/watchlist/${item.lot_id || item.id}`);
      }
      const ids = new Set(targets.map(i => i.lot_id || i.id));
      setWatchlist(prev => prev.filter(i => !ids.has(i.lot_id || i.id)));
      message.success(t('common.success'));
    } catch (err) {
      message.error(t('common.failed'));
    }
  };

  const isEmpty = displayItems.length === 0 && !loading;

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: BG, color: INK, fontSize: 14, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '14px 12px' : '24px 32px' }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: isMobile ? 14 : 24, gap: 14, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--serif,serif)', fontSize: isMobile ? 20 : 26, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: RED }}>♥</span> {t('watchlist.title')}
              <span style={{ fontFamily: 'monospace', fontSize: isMobile ? 13 : 16, fontWeight: 400, color: INK3 }}>（{totalCount}件）</span>
            </div>
            {!isMobile && <div style={{ fontSize: 13, color: INK3, marginTop: 4 }}>{t('watchlist.subtitle')}</div>}
          </div>
          {/* 批量操作仅桌面端显示，移动端保持清爽（用逐条 ✕ 删除） */}
          {!isMobile && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => navigate('/auction-hub')} style={btnStyle}>+ {t('watchlist.addMore')}</button>
            {activeTab === 'active' && (
              <button onClick={handleMandateAll} style={{ ...btnStyle, background: INK, color: '#fff', borderColor: INK }}>
                🔨 {t('watchlist.mandateAll')}
              </button>
            )}
            <button onClick={handleClearAll} style={{ ...btnStyle, color: RED, borderColor: 'rgba(192,57,43,.2)' }}>
              {t('watchlist.clearAll')}
            </button>
          </div>
          )}
        </div>

        {/* Stats strip — 桌面端显示；移动端隐藏以保持清爽 */}
        {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {(activeTab === 'active' ? [
            { val: activeItems.length, label: t('watchlist.activeWatches'), color: INK },
            { val: liveCount,          label: t('watchlist.liveNow'),   color: GRN },
            { val: soonCount,          label: t('watchlist.startingSoon'),   color: GOLD },
            { val: mandatedCount,      label: t('watchlist.mandateSubmitted'), color: RED },
          ] : [
            { val: endedItems.length,                          label: t('watchlist.endedWatches'), color: INK3 },
            { val: endedItems.filter(i => !!i.mandate).length,  label: t('watchlist.mandated'),     color: GRN },
            { val: endedItems.filter(i => !i.mandate).length,  label: t('watchlist.notMandated'),     color: INK4 },
            { val: totalCount,                                  label: t('watchlist.allWatches'),   color: INK },
          ]).map(stat => (
            <div key={stat.label} style={{
              background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4,
              padding: '14px 16px', textAlign: 'center', boxShadow: '0 2px 12px rgba(26,22,18,.08)',
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.val}</div>
              <div style={{ fontSize: 11, color: INK3, marginTop: 3 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        )}

        {/* Tabs + Sort */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: '#EDEAE3', borderRadius: 4, padding: 3, gap: 2, width: 'fit-content' }}>
          {[
            { key: 'active', label: t('session.inProgress'), count: activeItems.length },
            { key: 'ended',  label: t('browse.ended'), count: endedItems.length },
          ].map(tab => (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '7px 24px', fontSize: 13, cursor: 'pointer', borderRadius: 3,
                color: activeTab === tab.key ? INK : INK3,
                fontWeight: activeTab === tab.key ? 600 : 400,
                background: activeTab === tab.key ? '#fff' : 'transparent',
                boxShadow: activeTab === tab.key ? '0 1px 4px rgba(26,22,18,.08)' : 'none',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {tab.label}
              <span style={{
                fontSize: 11, fontFamily: 'monospace',
                color: activeTab === tab.key ? INK3 : INK4,
              }}>
                {tab.count}
              </span>
            </div>
          ))}
        </div>
        {/* Sort control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          {[
            { key: 'time_left', label: t('watchlist.sortTimeLeft') },
            { key: 'watched',   label: t('watchlist.sortWatched') },
          ].map(s => (
            <span
              key={s.key}
              onClick={() => setSortKey(s.key)}
              style={{
                fontSize: 13, cursor: 'pointer',
                color: sortKey === s.key ? GOLD : INK3,
                fontWeight: sortKey === s.key ? 600 : 400,
                borderBottom: sortKey === s.key ? `2px solid ${GOLD}` : '2px solid transparent',
                paddingBottom: 2,
              }}
            >{s.label}</span>
          ))}
        </div>
        </div>

        {/* 收藏内搜索 */}
        {watchlist.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            padding: '0 14px', height: 38, background: '#fff', border: `1px solid ${BD2}`, borderRadius: 19,
          }}>
            <span style={{ fontSize: 14, color: INK4 }}>🔍</span>
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder={t('watchlist.searchPlaceholder')}
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: INK }}
            />
            {keyword && <span onClick={() => setKeyword('')} style={{ fontSize: 13, color: INK4, cursor: 'pointer', padding: '0 4px' }}>✕</span>}
          </div>
        )}

        {/* Content */}
        <Spin spinning={loading}>
          {isEmpty ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', color: INK3 }}>
              <div style={{ fontSize: 56, marginBottom: 18, opacity: 0.25 }}>
                {activeTab === 'active' ? '♡' : '🔔'}
              </div>
              <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 20, fontWeight: 600, color: INK2, marginBottom: 8 }}>
                {activeTab === 'active' ? t('watchlist.emptyActive') : t('watchlist.emptyEnded')}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                {activeTab === 'active'
                  ? t('watchlist.emptyActiveDesc')
                  : t('watchlist.emptyEndedDesc')}
              </div>
              {activeTab === 'active' && (
                <button onClick={() => navigate('/auction-hub')} style={{
                  ...btnStyle, background: INK, color: '#fff', borderColor: INK, marginTop: 20,
                }}>{t('watchlist.goToBrowse')}</button>
              )}
            </div>
          ) : isMobile ? (
            /* 移动端：千旗式紧凑横排清单，按拍卖行分组 */
            (() => {
              const byHouse = [];
              const idx = {};
              displayItems.forEach(item => {
                const h = item.house_name || item.house_code || t('watchlist.uncategorized');
                if (idx[h] === undefined) { idx[h] = byHouse.length; byHouse.push({ house: h, items: [] }); }
                byHouse[idx[h]].items.push(item);
              });
              return byHouse.map(g => (
                <div key={g.house} style={{ marginBottom: 16, background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: BG3, borderBottom: `1px solid ${BD2}` }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: .5 }}>{g.house}</span>
                    <span style={{ fontSize: 11, color: INK4 }}>{g.items.length}{t('watchlist.itemsWatched')}</span>
                  </div>
                  {g.items.map(item => (
                    <CompactRow
                      key={item.lot_id || item.id}
                      item={item}
                      onRemove={handleRemove}
                      ended={activeTab === 'ended'}
                      jpyCnyRate={JPY_CNY}
                    />
                  ))}
                </div>
              ));
            })()
          ) : (
            groups.map((group, gi) => (
              <div key={gi} style={{ marginBottom: 28 }}>
                <SessionGroupHeader
                  group={group}
                  onMandateGroup={handleMandateGroup}
                  ended={activeTab === 'ended'}
                />
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 12, border: `1px solid ${BD2}`, borderTop: 'none',
                  borderRadius: '0 0 4px 4px', padding: 14, background: '#fff',
                }}>
                  {group.items.map(item => (
                    <WatchlistCard
                      key={item.lot_id || item.id}
                      item={item}
                      onRemove={handleRemove}
                      onMandate={handleMandate}
                      ended={activeTab === 'ended'}
                      jpyCnyRate={JPY_CNY}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </Spin>
      </div>

      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}`}</style>
    </div>
  );
}

const btnStyle = {
  padding: '8px 18px', borderRadius: 3, fontSize: 13, fontWeight: 500,
  cursor: 'pointer', border: `1px solid #D8D2C8`, background: '#fff', color: '#3D3830',
};
